import React from 'react';
import { Box, useTheme } from '@mui/material';

interface LogoProps {
  /**
   * Variant loga - icon (samo ikonica), horizontal (ikonica+tekst), ili full (kompletan)
   */
  variant?: 'icon' | 'horizontal' | 'full';

  /**
   * Color scheme - light (za tamne pozadine) ili dark (za svetle pozadine)
   * Ako nije definisano, automatski detektuje iz theme.palette.mode
   */
  colorScheme?: 'light' | 'dark' | 'auto';

  /**
   * Visina loga u pikselima
   */
  height?: number;

  /**
   * Širina loga u pikselima (opciono, ako nije definisano koristi auto)
   */
  width?: number | 'auto';

  /**
   * Alt text za pristupačnost
   */
  alt?: string;

  /**
   * Da li da fallback na tekst ako slika nije dostupna
   */
  enableTextFallback?: boolean;

  /**
   * Dodatni sx stilovi
   */
  sx?: any;

  /**
   * onClick handler
   */
  onClick?: () => void;
}

/**
 * Logo komponenta koja automatski bira pravu verziju loga na osnovu:
 * - variant (icon, horizontal, full)
 * - colorScheme (light za tamne pozadine, dark za svetle pozadine)
 * - theme mode (light/dark)
 *
 * Fallback hijerarhija:
 * 1. SVG verzija (preporučeno)
 * 2. PNG verzija najbliže veličine
 * 3. Text fallback "OmniPass"
 *
 * @example
 * // Icon za AppBar (tamna pozadina)
 * <Logo variant="icon" colorScheme="light" height={40} />
 *
 * // Horizontal logo za sidebar
 * <Logo variant="horizontal" colorScheme="light" height={50} />
 *
 * // Full logo za login stranicu
 * <Logo variant="full" height={400} />
 */
const Logo: React.FC<LogoProps> = ({
  variant = 'horizontal',
  colorScheme = 'auto',
  height = 40,
  width = 'auto',
  alt = 'OmniPass',
  enableTextFallback = true,
  sx,
  onClick
}) => {
  const theme = useTheme();

  // Determine color scheme
  const resolvedColorScheme = (() => {
    if (colorScheme !== 'auto') return colorScheme;
    return theme.palette.mode === 'dark' ? 'light' : 'dark';
  })();

  // Helper: Get optimal PNG size for icon variant
  const getIconSize = (height: number): string => {
    if (height <= 40) return '40';
    if (height <= 60) return '60';
    if (height <= 80) return '80';
    if (height <= 120) return '120';
    if (height <= 160) return '160';
    return '256';
  };

  // Helper: Get optimal PNG size for horizontal variant
  const getHorizontalSize = (height: number): string => {
    if (height <= 40) return '200x40';
    if (height <= 50) return '250x50';
    if (height <= 60) return '300x60';
    if (height <= 80) return '400x80';
    return '500x100';
  };

  // Helper: Get optimal PNG size for full variant
  const getFullSize = (height: number): string => {
    if (height <= 300) return '300';
    if (height <= 400) return '400';
    if (height <= 500) return '500';
    if (height <= 600) return '600';
    return '800';
  };

  // Build logo path with fallback hierarchy
  const getLogoPath = (): string => {
    const colorPrefix = resolvedColorScheme === 'light' ? 'logo-light' : 'logo-dark';

    // Determine optimal PNG size based on variant and height
    let pngSize = '';
    if (variant === 'icon') {
      pngSize = getIconSize(height);
    } else if (variant === 'horizontal') {
      pngSize = getHorizontalSize(height);
    } else if (variant === 'full') {
      pngSize = getFullSize(height);
    }

    // Use PNG directly (user created PNG files, not SVG)
    return `/images/logo/${colorPrefix}-${variant}-${pngSize}.png`;
  };

  const logoPath = getLogoPath();

  // Fallback sources (without color scheme) - also use smart sizing
  const getFallbackSize = () => {
    if (variant === 'icon') return height <= 60 ? '60' : '80';
    if (variant === 'horizontal') return height <= 50 ? '250x50' : '300x60';
    return height <= 400 ? '400' : '600';
  };

  const fallbackSources = [
    `/images/logo/logo-${variant}-${getFallbackSize()}.png`,
  ];

  const [imageError, setImageError] = React.useState(false);
  const [currentSrcIndex, setCurrentSrcIndex] = React.useState(0);

  const handleImageError = () => {
    if (currentSrcIndex < fallbackSources.length - 1) {
      setCurrentSrcIndex(prev => prev + 1);
    } else {
      setImageError(true);
    }
  };

  // Text fallback ako ni jedan image nije dostupan
  if (imageError && enableTextFallback) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: height,
          width: width === 'auto' ? 'auto' : width,
          fontWeight: 700,
          fontSize: (() => {
            if (variant === 'icon') return '16px';
            if (variant === 'horizontal') return '20px';
            return '32px';
          })(),
          color: resolvedColorScheme === 'light' ? '#FFFFFF' : theme.palette.primary.main,
          fontFamily: theme.typography.fontFamily,
          letterSpacing: '-0.02em',
          cursor: onClick ? 'pointer' : 'default',
          userSelect: 'none',
          ...sx
        }}
        onClick={onClick}
      >
        {variant === 'icon' ? 'OP' : 'OmniPass'}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={currentSrcIndex === 0 ? logoPath : fallbackSources[currentSrcIndex - 1]}
      alt={alt}
      onError={handleImageError}
      onClick={onClick}
      sx={{
        height: height,
        width: width,
        objectFit: 'contain',
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        display: 'block',
        ...sx
      }}
    />
  );
};

export default Logo;
